// Sistema de Engagement Scoring v2
// Score (0-100) basado en: bids (principal), visitas (recurrencia), contenido (exploración)
// El score se calcula por bandas según el estado de conversión del comprador

import { supabase } from '../../../supabaseClient';
import { getInviteEngagementEvents } from './engagementEvents';

/**
 * Tipos de eventos que cuentan como visita
 * page_view = carga inicial de página
 * page_return = usuario vuelve a la página (retorno)
 */
const VISIT_EVENT_TYPES = ['page_view', 'page_return'];

/**
 * Calcular días únicos de visita desde eventos
 * @param {Array} events - Eventos del invitado
 * @returns {number} Número de días únicos con visitas
 */
export const calculateUniqueDays = (events) => {
  const visitEvents = events.filter(e => VISIT_EVENT_TYPES.includes(e.event_type));
  const uniqueDates = new Set(
    visitEvents.map(e => new Date(e.created_at).toDateString())
  );
  return uniqueDates.size;
};

/**
 * Calcular visitas únicas agrupadas por ventana de tiempo
 * Agrupa eventos page_view/page_return que ocurren dentro de una ventana de tiempo
 * Esto evita que recargas o cambios de pestaña rápidos inflen el contador
 *
 * @param {Array} events - Eventos del invitado
 * @param {number} windowMinutes - Ventana de tiempo en minutos para agrupar (default: 5)
 * @returns {number} Número de visitas únicas
 */
export const calculateUniqueVisits = (events, windowMinutes = 5) => {
  const visitEvents = events
    .filter(e => VISIT_EVENT_TYPES.includes(e.event_type))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  if (visitEvents.length === 0) return 0;

  let uniqueVisits = 1;
  let lastVisitTime = new Date(visitEvents[0].created_at);

  for (let i = 1; i < visitEvents.length; i++) {
    const currentTime = new Date(visitEvents[i].created_at);
    const diffMinutes = (currentTime - lastVisitTime) / 1000 / 60;

    if (diffMinutes > windowMinutes) {
      uniqueVisits++;
      lastVisitTime = currentTime;
    }
  }

  return uniqueVisits;
};

/**
 * Calcular Visit Score (0-100)
 * Combina días únicos (recurrencia) y visitas totales (intensidad)
 * Base de 10 pts por haber entrado al menos una vez
 *
 * @param {number} uniqueDays - Días únicos de visita
 * @param {number} visits - Visitas únicas (agrupadas por ventana de 5 min)
 * @returns {number} Visit score (0-100)
 */
export const calculateVisitScore = (uniqueDays, visits) => {
  if (visits === 0) return 0;
  return Math.min(100, 10 + (uniqueDays * 15) + (visits * 3));
};

/**
 * Calcular Content Score (0-100)
 * Cada acción cuenta una sola vez. Sin plano (es vista por defecto).
 *
 * Distribución:
 * - Documento abierto: +40 (el más valioso, busca info seria)
 * - Video visto: +25 (interés en ver la propiedad)
 * - Galería abierta: +20 (quiere ver fotos)
 * - Cambio de pestaña: +15 (exploró más allá de lo básico)
 *
 * @param {Array} events - Eventos del invitado
 * @returns {number} Content score (0-100)
 */
export const calculateContentScore = (events) => {
  let score = 0;

  // Documento: +40 pts
  if (events.some(e => e.event_type === 'additional_doc_view')) score += 40;

  // Video: +25 pts
  if (events.some(e => e.event_type === 'video_view_start')) score += 25;

  // Galería: +20 pts
  if (events.some(e =>
    e.event_type === 'photo_view_start' ||
    e.event_type === 'photo_session_summary'
  )) score += 20;

  // Cambio de pestaña (descripción): +15 pts
  if (events.some(e => e.event_type === 'description_read')) score += 15;

  return Math.min(score, 100);
};

/**
 * Determinar estado de conversión y número de bids
 * @param {Array} events - Eventos del invitado
 * @param {boolean} hasBid - Si tiene puja confirmada
 * @param {number} bidCount - Número de pujas
 * @returns {'bid' | 'attempted' | 'watching'} Estado de conversión
 */
export const determineConversionStatus = (events, hasBid) => {
  if (hasBid) return 'bid';

  const hasAttempted = events.some(e =>
    e.event_type === 'bid_submit_attempt_failed' ||
    e.event_type === 'bid_form_focus' ||
    e.event_type === 'bid_amount_changed' ||
    e.event_type === 'bid_confirm_modal_opened' ||
    e.event_type === 'bid_confirm_modal_abandoned'
  );

  if (hasAttempted) return 'attempted';

  return 'watching';
};

/**
 * Calcular Engagement Score (0-100)
 * Sistema por bandas según estado de conversión:
 *
 * - 2+ bids     →  100 (interés máximo confirmado)
 * - 1 bid       →  85 + engagementBase × 0.10  (85-95)
 * - attempted   →  30 + engagementBase × 0.45  (30-75)
 * - watching    →  engagementBase × 0.60        (0-60)
 *
 * engagementBase = visitScore × 0.80 + contentScore × 0.20
 *
 * @param {Array} events - Eventos del invitado
 * @param {number} bidCount - Número de pujas confirmadas
 * @param {string} conversionStatus - Estado de conversión
 * @returns {number} Engagement score (0-100)
 */
export const calculateInterestScore = (events, bidCount = 0, conversionStatus = 'watching') => {
  const uniqueDays = calculateUniqueDays(events);
  const visits = calculateUniqueVisits(events, 5);
  const visitScore = calculateVisitScore(uniqueDays, visits);
  const contentScore = calculateContentScore(events);

  const engagementBase = (visitScore * 0.80) + (contentScore * 0.20);

  // 2+ bids: interés máximo confirmado
  if (bidCount >= 2) return 100;

  // 1 bid: arranca en 85, engagement sube hasta 95
  if (bidCount === 1) return Math.round(85 + engagementBase * 0.10);

  // Intentó pujar: arranca en 30, engagement sube hasta 75
  if (conversionStatus === 'attempted') return Math.round(30 + engagementBase * 0.45);

  // Solo mira: engagement puro, max 60
  return Math.round(engagementBase * 0.60);
};

/**
 * Calcular engagement score de un invitado (LEGACY - mantiene compatibilidad)
 * Llama a función RPC de Supabase si existe, sino calcula en client
 */
export const calculateEngagementScore = async (propertyId, inviteToken) => {
  try {
    const events = await getInviteEngagementEvents(propertyId, inviteToken);

    // Prioridad: bids (migradas) > blind_offers (fase ciega)
    const { data: bids } = await supabase
      .from('bids')
      .select('id')
      .eq('property_id', propertyId)
      .eq('buyer_email', inviteToken);

    let bidCount = bids?.length || 0;

    if (bidCount === 0) {
      const { data: blindOffers } = await supabase
        .from('blind_offers')
        .select('id')
        .eq('property_id', propertyId)
        .eq('buyer_email', inviteToken)
        .eq('is_verified', true);

      bidCount = blindOffers?.length || 0;
    }

    const hasBid = bidCount > 0;
    const conversionStatus = determineConversionStatus(events, hasBid);

    return calculateInterestScore(events, bidCount, conversionStatus);
  } catch (error) {
    console.error('Error calculando engagement score:', error);
    return 0;
  }
};

/**
 * Calcular métricas completas de scoring para un invitado
 * @param {string} propertyId - ID de la propiedad
 * @param {string} inviteToken - Token del invitado
 * @returns {Object} Métricas de scoring completas
 */
export const calculateFullScoringMetrics = async (propertyId, inviteToken) => {
  try {
    const events = await getInviteEngagementEvents(propertyId, inviteToken);

    // Prioridad: bids (migradas) > blind_offers (fase ciega)
    const { data: bids } = await supabase
      .from('bids')
      .select('id')
      .eq('property_id', propertyId)
      .eq('buyer_email', inviteToken);

    let bidCount = bids?.length || 0;

    if (bidCount === 0) {
      const { data: blindOffers } = await supabase
        .from('blind_offers')
        .select('id')
        .eq('property_id', propertyId)
        .eq('buyer_email', inviteToken)
        .eq('is_verified', true);

      bidCount = blindOffers?.length || 0;
    }

    const hasBid = bidCount > 0;

    const visits = calculateUniqueVisits(events, 5);
    const uniqueDays = calculateUniqueDays(events);
    const conversionStatus = determineConversionStatus(events, hasBid);
    const interestScore = calculateInterestScore(events, bidCount, conversionStatus);

    const biddingAttempts = events.filter(e =>
      e.event_type === 'bid_submit_attempt_failed' ||
      e.event_type === 'bid_submit_success' ||
      e.event_type === 'bid_confirm_modal_opened'
    ).length;

    return {
      interestScore,
      conversionStatus,
      uniqueDays,
      visits,
      bidCount,
      biddingAttempts,
      hasBid,
      score: interestScore,
    };
  } catch (error) {
    console.error('Error calculando métricas de scoring:', error);
    return {
      interestScore: 0,
      conversionStatus: 'watching',
      uniqueDays: 0,
      visits: 0,
      bidCount: 0,
      biddingAttempts: 0,
      hasBid: false,
      score: 0,
    };
  }
};
